import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '@/components/Breadcrumb';
import { Scale, Check, X, ArrowRight, Info } from 'lucide-react';
import { getCompetitor } from '@/data/platformSeoData';

export default function ComparePage() {
  const { competitor } = useParams();
  const data = getCompetitor(competitor);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="compare-error">
        Comparison not found. <Link to="/" className="text-sky-600 hover:underline ml-2">Back home</Link>
      </div>
    );
  }

  const canonical = `https://docsite.care/compare/${data.competitor_slug}`;
  const title = `DocSite vs ${data.name} | Which is better for doctors?`;
  const description = `A factual, category-level comparison of DocSite and ${data.name}. Pricing, ownership, SEO, commission — the dimensions that matter for a doctor choosing a platform.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    author: { '@type': 'Organization', name: 'DocSite' },
    publisher: {
      '@type': 'Organization', name: 'DocSite',
      logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' },
    },
    mainEntityOfPage: canonical,
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-sky-50 via-white to-amber-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16">
          <Breadcrumb items={[
            { label: 'DocSite', to: '/' },
            { label: 'Compare', to: '/resources' },
            { label: data.name },
          ]} />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mt-6 mb-3 flex items-center gap-1.5">
            <Scale size={12} /> Comparison
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="compare-h1">
            DocSite vs {data.name}
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl">{data.summary}</p>
          <p className="text-xs text-slate-400 mt-3">{data.name} positioning: <span className="text-slate-600">{data.type}</span></p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="compare-table">
          <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide font-bold">
            <div className="p-4 text-slate-600">Dimension</div>
            <div className="p-4 text-sky-700 border-l border-slate-200">DocSite</div>
            <div className="p-4 text-slate-600 border-l border-slate-200">{data.name}</div>
          </div>
          {data.positioning.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} border-b border-slate-100 last:border-b-0`}>
              <div className="p-4 text-slate-700 font-medium">{row.dim}</div>
              <div className="p-4 text-slate-800 border-l border-slate-100 flex items-start gap-2">
                <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" /><span>{row.us}</span>
              </div>
              <div className="p-4 text-slate-600 border-l border-slate-100 flex items-start gap-2">
                <X size={16} className="text-slate-400 flex-shrink-0 mt-0.5" /><span>{row.them}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-6 md:py-12">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5" data-testid="compare-pick-us">
            <p className="text-xs uppercase tracking-wide font-bold text-sky-700 mb-2">Pick DocSite if</p>
            <p className="text-sm text-slate-700 leading-relaxed">{data.when_to_pick_us}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5" data-testid="compare-pick-them">
            <p className="text-xs uppercase tracking-wide font-bold text-slate-600 mb-2">Pick {data.name} if</p>
            <p className="text-sm text-slate-700 leading-relaxed">{data.when_to_pick_them}</p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-6">
        <div className="flex items-start gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600 leading-relaxed">{data.disclaimer}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12 text-center">
        <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose what's right for your practice.</h2>
        <p className="text-sm text-slate-600 mb-6">DocSite is Rs. 0 to start. No credit card, no aggregator lock-in.</p>
        <Link to="/register?as=doctor&from=compare" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-sky-600 transition-colors" data-testid="compare-cta">
          Start my DocSite free <ArrowRight size={14} />
        </Link>
      </section>
    </div>
  );
}
